import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy,
  Users,
  Bell,
  Video,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Award,
} from 'lucide-react';

interface OnboardingSlide {
  id: number;
  icon: React.ElementType;
  title: string;
  description: string;
  gradient: string;
  iconGradient: string;
}

const slides: OnboardingSlide[] = [
  {
    id: 1,
    icon: Trophy,
    title: 'Track Tournaments',
    description: 'Follow live brackets, scores, and results from tournaments around the world in real-time.',
    gradient: 'from-amber-500 via-orange-500 to-rose-500',
    iconGradient: 'from-amber-400 to-orange-500',
  },
  {
    id: 2,
    icon: Award,
    title: 'Follow Leagues',
    description: 'Track your favorite leagues with live standings, schedules, and season stats.',
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    iconGradient: 'from-emerald-400 to-teal-500',
  },
  {
    id: 3,
    icon: Users,
    title: 'Follow Athletes',
    description: 'Keep up with your favorite competitors. Get notified when they\'re about to compete.',
    gradient: 'from-teal-500 via-cyan-500 to-blue-500',
    iconGradient: 'from-teal-400 to-cyan-500',
  },
  {
    id: 4,
    icon: Bell,
    title: 'Never Miss a Match',
    description: 'Receive instant notifications when athletes are on deck or matches are about to start.',
    gradient: 'from-violet-500 via-purple-500 to-fuchsia-500',
    iconGradient: 'from-violet-400 to-purple-500',
  },
  {
    id: 5,
    icon: Video,
    title: 'Watch Live',
    description: 'Stream matches live with integrated scoreboards and create shareable highlight clips.',
    gradient: 'from-rose-500 via-pink-500 to-red-500',
    iconGradient: 'from-rose-400 to-pink-500',
  },
];

const STORAGE_KEY = 'podium_mobile_onboarding_complete';

export function MobileOnboarding({ onComplete }: { onComplete: () => void }) {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && currentSlide < slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handleGetStarted = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onComplete();
    navigate('/register');
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onComplete();
  };

  const handleSignIn = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onComplete();
    navigate('/login');
  };

  const slide = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;
  const Icon = slide.icon;

  return (
    <div 
      className="fixed inset-0 bg-slate-950 flex flex-col overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Background gradient effect */}
      <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} opacity-10 transition-all duration-700`} />
      
      {/* Animated orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 -left-20 w-64 h-64 bg-gradient-to-br ${slide.gradient} rounded-full blur-3xl opacity-30 animate-pulse`} />
        <div className={`absolute bottom-1/3 -right-20 w-72 h-72 bg-gradient-to-br ${slide.gradient} rounded-full blur-3xl opacity-20 animate-pulse delay-500`} />
      </div>

      {/* Skip button */}
      {!isLastSlide && (
        <button
          onClick={handleSkip}
          className="absolute top-14 right-4 z-20 min-h-[44px] min-w-[44px] px-4 py-2 text-slate-400 hover:text-white text-sm font-medium transition-colors rounded-lg"
        >
          Skip
        </button>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 relative z-10">
        {/* Icon */}
        <div className="mb-8 relative">
          <div className={`absolute inset-0 bg-gradient-to-br ${slide.iconGradient} rounded-3xl blur-xl opacity-50 scale-110`} />
          <div className={`relative w-24 h-24 bg-gradient-to-br ${slide.iconGradient} rounded-3xl flex items-center justify-center shadow-2xl`}>
            <Icon className="w-12 h-12 text-white" strokeWidth={1.5} />
          </div>
        </div>

        {/* Title & Description */}
        <div className="text-center max-w-sm">
          <h1 className="text-3xl font-bold text-white mb-4 tracking-tight">
            {slide.title}
          </h1>
          <p className="text-lg text-slate-300 leading-relaxed">
            {slide.description}
          </p>
        </div>
      </div>

      {/* Bottom section */}
      <div className="relative z-10 px-8 pb-12 safe-area-inset-bottom">
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-3 mb-8">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full"
            >
              <span
                className={`block rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? `h-2.5 w-10 bg-gradient-to-r ${slide.gradient}`
                    : 'h-2.5 w-2.5 bg-slate-600 hover:bg-slate-500'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Action buttons */}
        {isLastSlide ? (
          <div className="space-y-3">
            <button
              onClick={handleGetStarted}
              className={`w-full min-h-[52px] py-4 px-6 bg-gradient-to-r ${slide.gradient} rounded-2xl font-semibold text-white text-lg flex items-center justify-center gap-2 shadow-lg shadow-rose-500/25 active:scale-[0.98] transition-transform`}
            >
              <Sparkles className="w-5 h-5" />
              Get Started
            </button>
            <button
              onClick={handleSignIn}
              className="w-full min-h-[52px] py-4 px-6 bg-slate-800/50 border border-slate-700 rounded-2xl font-medium text-slate-300 text-lg hover:bg-slate-800 transition-colors active:scale-[0.98]"
            >
              I already have an account
            </button>
          </div>
        ) : (
          <button
            onClick={handleNext}
            className={`w-full min-h-[52px] py-4 px-6 bg-gradient-to-r ${slide.gradient} rounded-2xl font-semibold text-white text-lg flex items-center justify-center gap-2 shadow-lg active:scale-[0.98] transition-transform`}
          >
            Continue
            <ChevronRight className="w-5 h-5" />
          </button>
        )}

        {/* Sign in link - only on non-last slides */}
        {!isLastSlide && (
          <button
            onClick={handleSignIn}
            className="w-full mt-4 min-h-[44px] py-3 text-slate-400 text-sm font-medium flex items-center justify-center gap-1 hover:text-white transition-colors"
          >
            Already have an account?
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Check if the mobile onboarding has been completed
 */
export function hasMobileOnboardingCompleted(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

/**
 * Reset mobile onboarding state (useful for testing)
 */
export function resetMobileOnboarding(): void {
  localStorage.removeItem(STORAGE_KEY);
}

